/**
 * Sales Pipeline — top-of-funnel Kanban board.
 *
 * Columns: Cold · Warm · Hot · Qualified · Won · Lost. Each lead card
 * shows organisation, contact, estimated value and next action. Clicks
 * navigate to the dedicated LeadEditorPage; "New lead" navigates to
 * `/sales-pipeline/new`. Stage moves happen inline via a per-card
 * dropdown, delete via a per-card icon.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Loader2, RefreshCw, TrendingUp, Search, Trash2, Mail,
  Phone, Calendar, DollarSign, User, Building2, FileText, Gift,
  ChevronDown, ChevronRight,
} from "lucide-react";

const ACTIVE_STAGES = [
  { key: "cold", label: "Cold", color: "bg-sky-100 text-sky-800", accent: "border-sky-300" },
  { key: "warm", label: "Warm", color: "bg-amber-100 text-amber-800", accent: "border-amber-300" },
  { key: "hot", label: "Hot", color: "bg-orange-200 text-orange-900", accent: "border-orange-400" },
  { key: "qualified", label: "Qualified", color: "bg-violet-100 text-violet-800", accent: "border-violet-300" },
];
const CLOSED_STAGES = [
  { key: "won", label: "Won", color: "bg-emerald-100 text-emerald-800", accent: "border-emerald-300" },
  { key: "lost", label: "Lost", color: "bg-zinc-100 text-zinc-500 line-through", accent: "border-zinc-200" },
];
const STAGES = [...ACTIVE_STAGES, ...CLOSED_STAGES];

const fmtCurrency = (v) =>
  v == null ? "—"
    : new Intl.NumberFormat("en-AU", {
        style: "currency", currency: "AUD", maximumFractionDigits: 0,
      }).format(Number(v) || 0);

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" }); }
  catch { return ""; }
};

/* ── Lead card ─────────────────────────────────────────────────── */
function LeadCard({ lead, onOpen, onMove, onDelete }) {
  const stage = STAGES.find((s) => s.key === lead.stage) || STAGES[0];
  const contactFullName = [lead.contact_first_name, lead.contact_last_name]
    .filter(Boolean).join(" ").trim();
  const referrerFullName = [lead.referred_by_first_name, lead.referred_by_last_name]
    .filter(Boolean).join(" ").trim();
  return (
    <div
      className={`bg-white rounded-sm border ${stage.accent} p-2.5 space-y-1.5 cursor-pointer hover:shadow-sm transition-shadow`}
      onClick={() => onOpen(lead)}
      data-testid={`lead-card-${lead.lead_id}`}>
      <div className="flex items-start justify-between gap-1.5">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{lead.company_name}</div>
          {contactFullName && (
            <div className="text-xs text-zinc-500 flex items-center gap-1 truncate">
              <User className="h-3 w-3 shrink-0" />{contactFullName}
            </div>
          )}
        </div>
        {lead.estimated_value != null && (
          <div className="text-[11px] font-bold text-emerald-700 shrink-0">
            {fmtCurrency(lead.estimated_value)}
          </div>
        )}
      </div>

      {(lead.contact_email || lead.contact_phone) && (
        <div className="flex items-center gap-2 text-[10px] text-zinc-500 flex-wrap">
          {lead.contact_email && (
            <span className="flex items-center gap-1 truncate max-w-[140px]">
              <Mail className="h-2.5 w-2.5" />{lead.contact_email}
            </span>
          )}
          {lead.contact_phone && (
            <span className="flex items-center gap-1">
              <Phone className="h-2.5 w-2.5" />{lead.contact_phone}
            </span>
          )}
        </div>
      )}

      {lead.next_action && (
        <div className="text-[11px] text-zinc-700 bg-zinc-50 rounded px-1.5 py-1 border border-zinc-100">
          <div className="flex items-center gap-1 text-zinc-500 text-[9px] uppercase tracking-wide font-semibold">
            <Calendar className="h-2.5 w-2.5" />
            Next{lead.next_action_date ? ` · ${fmtDate(lead.next_action_date)}` : ""}
          </div>
          <div className="truncate">{lead.next_action}</div>
        </div>
      )}

      {lead.source && (
        <Badge variant="outline"
          className="text-[9px] rounded-sm border-zinc-200 bg-zinc-50 text-zinc-600">
          {lead.source}
        </Badge>
      )}

      {referrerFullName && (
        <div className="flex items-center gap-1 text-[10px] text-fuchsia-700"
          title={`Referred by ${referrerFullName}${lead.referred_by_company ? ` (${lead.referred_by_company})` : ""}`}>
          <Gift className="h-2.5 w-2.5" />
          <span className="truncate">Ref: {referrerFullName}</span>
        </div>
      )}

      <div className="flex items-center gap-1 pt-1 border-t border-zinc-100"
        onClick={(e) => e.stopPropagation()}>
        <Select value={lead.stage}
          onValueChange={(v) => onMove(lead, v)}>
          <SelectTrigger className="h-6 text-[10px] rounded-sm flex-1 min-w-0"
            data-testid={`lead-card-stage-${lead.lead_id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STAGES.map((s) => (
              <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0"
          onClick={() => onDelete(lead)}
          data-testid={`lead-card-delete-${lead.lead_id}`}>
          <Trash2 className="h-3 w-3 text-zinc-400 hover:text-red-600" />
        </Button>
      </div>
    </div>
  );
}

/* ── Column ────────────────────────────────────────────────────── */
function StageColumn({ stage, leads, valueSum, onOpen, onMove, onDelete }) {
  return (
    <div className="flex flex-col min-w-[260px] w-[260px]"
      data-testid={`stage-column-${stage.key}`}>
      <div className={`rounded-t-sm px-3 py-2 flex items-center justify-between ${stage.color}`}>
        <div className="font-semibold text-xs uppercase tracking-wide">
          {stage.label}
        </div>
        <div className="text-[10px] font-mono opacity-80">
          {leads.length} · {fmtCurrency(valueSum)}
        </div>
      </div>
      <div className="flex-1 bg-zinc-50 rounded-b-sm p-2 space-y-2 min-h-[100px] max-h-[calc(100vh-260px)] overflow-y-auto border border-t-0 border-zinc-200">
        {leads.length === 0 ? (
          <div className="text-[11px] text-zinc-400 italic text-center py-6">
            No leads
          </div>
        ) : leads.map((l) => (
          <LeadCard key={l.lead_id} lead={l}
            onOpen={onOpen} onMove={onMove} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────── */
export default function SalesPipelinePage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [closedOpen, setClosedOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/sales-pipeline/leads",
          { params: search ? { search } : {} }),
        api.get("/sales-pipeline/summary"),
      ]);
      setLeads(l.data.leads || []);
      setSummary(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load leads");
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    for (const l of leads) {
      const k = STAGES.find((s) => s.key === l.stage) ? l.stage : "cold";
      map[k].push(l);
    }
    return map;
  }, [leads]);

  const valueByStage = useMemo(() => {
    const map = {};
    for (const s of summary?.stages || []) map[s.stage] = s.value;
    if (Object.keys(map).length === 0) {
      for (const s of STAGES) {
        map[s.key] = (byStage[s.key] || [])
          .reduce((a, l) => a + (Number(l.estimated_value) || 0), 0);
      }
    }
    return map;
  }, [summary, byStage]);

  const openLead = (lead) => navigate(`/sales-pipeline/leads/${lead.lead_id}`);
  const openNew = () => navigate("/sales-pipeline/new");

  const moveLead = async (lead, newStage) => {
    if (newStage === lead.stage) return;
    setLeads((prev) => prev.map((l) =>
      l.lead_id === lead.lead_id ? { ...l, stage: newStage } : l));
    try {
      await api.put(`/sales-pipeline/leads/${lead.lead_id}`,
        { stage: newStage });
      const s = await api.get("/sales-pipeline/summary");
      setSummary(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Move failed");
      await load();
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.company_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/sales-pipeline/leads/${lead.lead_id}`);
      toast.success("Lead deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="sales-pipeline-page">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-gradient-to-br from-orange-500 to-red-600 text-white">
          <TrendingUp className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-[280px]">
          <div className="label-overline">Work</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">Sales Pipeline</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Track leads from first touch through to qualified — then hand
            them off to a Proposal. Move cards through the funnel as you
            work them; the summary above the board rolls up live totals.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm"
            onClick={load} disabled={loading}
            data-testid="pipeline-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button size="sm" className="rounded-sm bg-[#002FA7] hover:bg-[#001f70] text-white"
            onClick={openNew} data-testid="pipeline-new-lead-btn">
            <Plus className="h-4 w-4 mr-1.5" />New lead
          </Button>
        </div>
      </div>

      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3"
          data-testid="pipeline-summary">
          <Card className="rounded-sm border-zinc-200">
            <CardContent className="p-3">
              <div className="label-overline flex items-center gap-1.5">
                <Building2 className="h-3 w-3" /> Active leads
              </div>
              <div className="text-2xl font-bold mt-1">{summary.active_count}</div>
            </CardContent>
          </Card>
          <Card className="rounded-sm border-zinc-200">
            <CardContent className="p-3">
              <div className="label-overline flex items-center gap-1.5">
                <DollarSign className="h-3 w-3" /> Active pipeline value
              </div>
              <div className="text-2xl font-bold mt-1 text-emerald-700">
                {fmtCurrency(summary.active_value)}
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm border-zinc-200">
            <CardContent className="p-3">
              <div className="label-overline flex items-center gap-1.5">
                <FileText className="h-3 w-3" /> Won deals
              </div>
              <div className="text-2xl font-bold mt-1">{summary.won_count}</div>
            </CardContent>
          </Card>
          <Card className="rounded-sm border-zinc-200">
            <CardContent className="p-3">
              <div className="label-overline flex items-center gap-1.5">
                <DollarSign className="h-3 w-3" /> Won value
              </div>
              <div className="text-2xl font-bold mt-1 text-emerald-700">
                {fmtCurrency(summary.won_value)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by organisation, contact, or email…"
            className="rounded-sm pl-8 h-9"
            data-testid="pipeline-search-input" />
        </div>
      </div>

      {/* Board */}
      {loading && leads.length === 0 ? (
        <div className="p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin inline text-zinc-400" />
        </div>
      ) : (
        <div className="space-y-4" data-testid="pipeline-board">
          {/* Active pipeline: Cold · Warm · Hot · Qualified */}
          <div className="overflow-x-auto pb-2">
            <div className="flex gap-3">
              {ACTIVE_STAGES.map((s) => (
                <StageColumn key={s.key} stage={s}
                  leads={byStage[s.key] || []}
                  valueSum={valueByStage[s.key] || 0}
                  onOpen={openLead}
                  onMove={moveLead}
                  onDelete={deleteLead} />
              ))}
            </div>
          </div>

          {/* Closed pipeline: Won · Lost — collapsed by default */}
          <div className="border border-zinc-200 rounded-sm bg-white"
            data-testid="pipeline-closed-section">
            <button type="button"
              onClick={() => setClosedOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-zinc-50 transition-colors"
              data-testid="pipeline-closed-toggle"
              aria-expanded={closedOpen}>
              <div className="flex items-center gap-2">
                {closedOpen
                  ? <ChevronDown className="h-4 w-4 text-zinc-500" />
                  : <ChevronRight className="h-4 w-4 text-zinc-500" />}
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-700">
                  Closed
                </span>
                {CLOSED_STAGES.map((s) => (
                  <Badge key={s.key} variant="outline"
                    className={`rounded-sm text-[10px] ${s.color} border-transparent`}>
                    {s.label} · {(byStage[s.key] || []).length} · {fmtCurrency(valueByStage[s.key] || 0)}
                  </Badge>
                ))}
              </div>
              <span className="text-[11px] text-zinc-400">
                {closedOpen ? "Click to collapse" : "Click to expand"}
              </span>
            </button>
            {closedOpen && (
              <div className="overflow-x-auto p-3 pt-0 border-t border-zinc-100">
                <div className="flex gap-3">
                  {CLOSED_STAGES.map((s) => (
                    <StageColumn key={s.key} stage={s}
                      leads={byStage[s.key] || []}
                      valueSum={valueByStage[s.key] || 0}
                      onOpen={openLead}
                      onMove={moveLead}
                      onDelete={deleteLead} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
